import React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { ChevronDown, Check } from 'lucide-react';
import clsx from 'clsx';
import { Label } from './label';

const Select = SelectPrimitive.Root;

const SelectTrigger = React.forwardRef(
  (
    { className, label, error, helperText, id, placeholder, containerClassName, ...props },
    ref
  ) => {
    const triggerId =
      id || (label ? `select-${label.toLowerCase().replace(/\s+/g, '-')}` : undefined);

    const base =
      'flex w-full items-center justify-between rounded-xl border bg-white px-4 py-3 text-sm text-slate-900';
    const normal = 'border-slate-200 focus-visible:ring-indigo-500/50 focus-visible:border-indigo-500';
    const errorClasses = 'border-red-300 focus-visible:ring-red-500/50 focus-visible:border-red-500';

    return (
      <div className={clsx("w-full", containerClassName)}>
        {label && <Label htmlFor={triggerId}>{label}</Label>}
        <SelectPrimitive.Trigger
          ref={ref}
          id={triggerId}
          className={clsx(
            base,
            'focus-visible:outline-none focus-visible:ring-2',
            error ? errorClasses : normal,
            className
          )}
          {...props}
        >
          <SelectPrimitive.Value placeholder={placeholder} />
          <ChevronDown className="h-4 w-4 text-slate-400" />
        </SelectPrimitive.Trigger>
        {error && (
          <p className="mt-1 text-sm text-red-600">{error}</p>
        )}
        {!error && helperText && (
          <p className="mt-1 text-sm text-slate-500">{helperText}</p>
        )}
      </div>
    );
  }
);
SelectTrigger.displayName = 'SelectTrigger';

const SelectContent = React.forwardRef(({ className, ...props }, ref) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      ref={ref}
      className={clsx(
        'z-50 min-w-[8rem] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-md',
        className
      )}
      {...props}
    >
      <SelectPrimitive.Viewport className="p-1">
        {props.children}
      </SelectPrimitive.Viewport>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = 'SelectContent';

const SelectItem = React.forwardRef(({ className, children, description, ...props }, ref) => (
  <SelectPrimitive.Item
    ref={ref}
    className={clsx(
      'relative flex cursor-default select-none rounded-md pl-8 pr-3 py-1.5 text-sm text-slate-700',
      description ? 'flex-col items-start gap-0.5 py-2' : 'items-center',
      'focus:bg-slate-100 focus:text-slate-900 outline-none',
      'data-[state=checked]:bg-indigo-50 data-[state=checked]:text-indigo-700',
      className
    )}
    {...props}
  >
    <span className="absolute left-2 flex h-4 w-4 items-center justify-center">
      <SelectPrimitive.ItemIndicator>
        <Check className="h-4 w-4 text-indigo-500" />
      </SelectPrimitive.ItemIndicator>
    </span>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    {description ? <span className="text-[10px] text-slate-500">{description}</span> : null}
  </SelectPrimitive.Item>
));
SelectItem.displayName = 'SelectItem';

export { Select, SelectTrigger, SelectContent, SelectItem };
